import { AgeCounts, AgeGroup, AnandamelaLine, BookingRequest, BookingSelection, DayMeals, EventDay, MealType, Quote, QuoteLine, Reservation2026 } from './booking';

export const EVENT_NAME = 'Durga Puja 2026';
export const COLLECTION = 'durga_puja_2026-mealReservations'; // parent collection = event, one document per reservation
export const CONTACT_EMAIL = 'admin@sanskriti-hamburg.de';
// Shown in place of the in-app donation flow once every day and Anandamela are sold out - see soldOut on EventDay.
export const DONATION_URL = 'https://sanskriti-hamburg.de/support#give';

export const AGE_GROUPS: { id: AgeGroup; name: string; range: string }[] = [
  { id: 'infant', name: 'Infant', range: '0-5 yrs' },
  { id: 'kid', name: 'Kid', range: '6-14 yrs' },
  { id: 'adult', name: 'Adult', range: '14+ yrs' },
];
export const MEAL_TYPES: { id: MealType; name: string }[] = [
  { id: 'veg', name: 'Vegetarian' },
  { id: 'nonveg', name: 'Non-Vegetarian' },
];

// NOTE: prices below (except Navami, see below) are carried over from 2025, kept deliberately for the 2026 launch.
// Every price is spelled out here, per day / meal type / age group - nothing is inherited or defaulted from
// elsewhere. Infants are always 0. `nonveg` is optional per day (Ashtami has none).
// `items` is the static dish list shown to the guest alongside the menu; it plays no part in pricing or the invoice.
export const DAYS: EventDay[] = [
  {
    date: '2026-10-17', name: 'Shashti', dateLabel: 'Sat, 17 Oct 2026',
    soldOut: true, // at capacity - see the soldOut field doc on EventDay
    menus: {
      veg: {
        name: 'Vegetarian Thali', prices: { infant: 0, kid: 15, adult: 18 },
        items: ['Kolkata Veg Chop', 'Narkel diye Chholar Dal', 'Gorom Gorom Luchi', 'Basanti Pulao', 'Shahi Matar Paneer', 'Katharibhog Mishti'],
      },
      nonveg: {
        name: 'Non-Vegetarian Thali', prices: { infant: 0, kid: 15, adult: 20 },
        items: ['Kolkata Fish Fry', 'Narkel diye Chholar Dal', 'Gorom Gorom Luchi', 'Basanti Pulao', 'Royal Chicken Kosha', 'Katharibhog Mishti'],
      },
    },
  },
  {
    date: '2026-10-18', name: 'Saptami', dateLabel: 'Sun, 18 Oct 2026',
    soldOut: true, // at capacity - see the soldOut field doc on EventDay
    menus: {
      veg: {
        name: 'Vegetarian Thali', prices: { infant: 0, kid: 15, adult: 18 },
        items: ['Crispy Fulkopi Pakora', 'Bonedi Basanti Pulao', 'Aloo Dum', 'Shahi Paneer Rezala', 'Mixed Fruit Chutney', 'Royal Rasmalai'],
      },
      nonveg: {
        name: 'Non-Vegetarian Thali', prices: { infant: 0, kid: 15, adult: 22 },
        items: ['Kolkata Chicken Cutlet', 'Bonedi Basanti Pulao', 'Aloo Dum', 'Shahi Mutton Kosha', 'Mixed Fruit Chutney', 'Royal Rasmalai'],
      },
    },
  },
  {
    date: '2026-10-19', name: 'Ashtami', dateLabel: 'Mon, 19 Oct 2026',
    soldOut: true, // at capacity - see the soldOut field doc on EventDay
    menus: {
      veg: {
        name: 'Vegetarian Thali', prices: { infant: 0, kid: 14, adult: 15 },
        items: ['Crispy Beguni', 'Narkel diye Chholar Dal', 'Gorom Gorom Luchi', 'Bhoger Khichuri', 'Puja Special Labra', 'Anarosher Chutney', 'Kachagolla'],
      },
    },
  },
  {
    date: '2026-10-20', name: 'Navami', dateLabel: 'Tue, 20 Oct 2026',
    soldOut: true, // at capacity - see the soldOut field doc on EventDay
    menus: {
      veg: {
        name: 'Vegetarian Thali', prices: { infant: 0, kid: 15, adult: 18 },
        items: ['Kolkata Veg Chop', 'Gorom Bhaat', 'Shona Moong Dal', 'Sharod Badhakopi Aloo', 'Naborotno Malai Kofta', 'Kurkure Papad', 'Tok Jhal Mishti Chutney', 'Malai Langcha'],
      },
      nonveg: {
        name: 'Non-Vegetarian Thali', prices: { infant: 0, kid: 15, adult: 20 },
        items: ['Kolkata Fish Chop', 'Gorom Bhaat', 'Shona Moong Dal', 'Sharod Badhakopi Aloo', 'Rajbari Chingri Malaikari', 'Kurkure Papad', 'Tok Jhal Mishti Chutney', 'Malai Langcha'],
      },
    },
  },
];

// Short German explanation of each dish and its main ingredients, shown under the dish name on the booking page.
// Keyed by the exact dish name used in DAYS above - a renamed dish needs its key renamed here too.
export const DISH_DESCRIPTIONS_DE: Record<string, string> = {
  'Kolkata Veg Chop': 'Eine frittierte Krokette aus Kartoffeln, Roter Bete und indischen Gewürzen.',
  'Narkel diye Chholar Dal': 'Gelbe Kichererbsen-Linsen, gekocht mit frischer Kokosnuss und milden Gewürzen.',
  'Gorom Gorom Luchi': 'Knusprig frittiertes Weißmehlbrot, das beim Backen aufgeht.',
  'Basanti Pulao': 'Süßlich gewürzter Reis mit Ghee, Rosinen und Safran oder Kurkuma.',
  'Shahi Matar Paneer': 'Indischer Frischkäse und grüne Erbsen in cremiger Tomaten-Sahne-Sauce mit Garam Masala.',
  'Katharibhog Mishti': 'Eine traditionelle bengalische Süßspeise aus frischem Käse und Zucker.',
  'Kolkata Fish Fry': 'In Semmelbröseln paniertes und knusprig frittiertes Fischfilet.',
  'Royal Chicken Kosha': 'Hühnerfleisch, geschmort in würziger Zwiebel-Tomaten-Sauce mit Ingwer und Knoblauch.',
  'Crispy Fulkopi Pakora': 'Blumenkohlröschen in Kichererbsenmehl-Teig frittiert.',
  'Bonedi Basanti Pulao': 'Aromatischer gelber Reis mit Ghee, Rosinen und Gewürzen, nach Familienart zubereitet.',
  'Aloo Dum': 'Kleine Kartoffeln, geschmort in würziger Joghurt- und Tomatensauce.',
  'Shahi Paneer Rezala': 'Indischer Frischkäse in milder, cremiger Sauce mit Joghurt und Cashewnüssen.',
  'Mixed Fruit Chutney': 'Eine süß-saure Beilage aus gekochten Früchten, Zucker und Gewürzen.',
  'Royal Rasmalai': 'Weiche Käsebällchen in gesüßter, mit Kardamom aromatisierter Milch.',
  'Kolkata Chicken Cutlet': 'Paniertes und frittiertes Hähnchenfilet mit indischen Gewürzen.',
  'Shahi Mutton Kosha': 'Ziegen fleisch, geschmort in würziger Zwiebel-Tomaten-Sauce.',
  'Crispy Beguni': 'Auberginenscheiben in Kichererbsenmehl-Teig frittiert.',
  'Bhoger Khichuri': 'Reis und gelbe Linsen, mit Gemüse gekocht und während der Puja als Opfergabe serviert.',
  'Puja Special Labra': 'Ein gemischtes Gemüsecurry aus saisonalem Gemüse, traditionell zur Khichuri serviert.',
  'Anarosher Chutney': 'Eine süß-saure Ananas-Chutney, mit Gewürzen gekocht.',
  'Kachagolla': 'Eine weiche, milchbasierte bengalische Süßspeise aus frischem Käse und Zucker.',
  'Gorom Bhaat': 'Warm servierter, gedämpfter weißer Reis.',
  'Shona Moong Dal': 'Gelbe Mungbohnen-Linsen, mild gewürzt und sämig gekocht.',
  'Sharod Badhakopi Aloo': 'Weißkohl und Kartoffeln, zusammen mit milden Gewürzen geschmort.',
  'Naborotno Malai Kofta': 'Frittierte Gemüse-Käse-Bällchen in cremiger, leicht süßlicher Cashew-Sahne-Sauce.',
  'Kurkure Papad': 'Knusprig frittierte oder gegrillte Linsenchips.',
  'Tok Jhal Mishti Chutney': 'Eine süß-scharf-saure Tomaten-Chutney mit Gewürzen.',
  'Malai Langcha': 'Frittierte Milchbällchen, getränkt in süßem, mit Kardamom aromatisiertem Sirup.',
  'Kolkata Fish Chop': 'Eine frittierte Krokette aus zerkleinertem Fisch, Kartoffeln und Gewürzen.',
  'Rajbari Chingri Malaikari': 'Garnelen, geschmort in einer cremigen Kokosmilch-Sauce mit Gewürzen.',
};

/** Day 5: a flat per-person registration fee (infants free), not an entry or food fee - paying it is how a guest
 * commits to actually attending, and gives us a headcount we can promise our food vendors. It comes back in full,
 * in kind: at the frontdesk on arrival, each registered person is handed a coupon worth the same amount, to spend
 * at any stall - not a refund to the original payment method. */
export const ANANDAMELA = {
  date: '2026-10-21',
  name: 'Dashami - Anandamela',
  dateLabel: 'Wed, 21 Oct 2026',
  description: 'A registration fee, not an entry fee - it shows you’re committed to coming, so we can tell our food vendors how many people to expect. At the frontdesk on the day, each registered person gets a coupon worth the same amount, to spend at any stall.',
  prices: { infant: 0, kid: 5, adult: 5 } as AgeCounts,
  soldOut: true, // at capacity - see the soldOut field doc on EventDay; same treatment, just not an EventDay itself
};

// Shown to the guest on the review page, right before they pay. Applies to every reservation, paid or free -
// the invoice itself is a separate, payment-specific note (free reservations get no Stripe invoice at all).
export const BOOKING_POLICY = [
  'Reservations are final and non-refundable.',
  'A reservation made for one day cannot be moved to another day.',
  'A reservation cannot be transferred to another guest.',
];

const MAX_PER_CELL = 50;
const MAX_DONATION = 10000;
export const DEFAULT_DONATION = 50;
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const emptyCounts = (): AgeCounts => ({ infant: 0, kid: 0, adult: 0 });
export const emptyDayMeals = (): DayMeals => ({ veg: emptyCounts(), nonveg: emptyCounts() });
export const emptySelection = (): BookingSelection => ({
  meals: Object.fromEntries(DAYS.map((d) => [d.date, emptyDayMeals()])),
  anandamela: emptyCounts(),
  donation: 0,
});

export const eur = (cents: number) => `€${(cents / 100).toFixed(2)}`;
export const toCents = (euros: number) => Math.round(euros * 100);

// Stripe's standard fee for European cards. Informational only: the user pays exactly `totalCents`.
export const estimateFeeCents = (totalCents: number) => (totalCents > 0 ? Math.round(totalCents * 0.015) + 25 : 0);

const count = (sel: BookingSelection, date: string, type: MealType, age: AgeGroup) => sel.meals?.[date]?.[type]?.[age] ?? 0;
const isCount = (n: unknown) => typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= MAX_PER_CELL;

export function validateCustomer(c?: { name?: string; email?: string }): string | null {
  const name = (c?.name ?? '').trim();
  if (name.length < 2 || name.length > 100) return 'Please enter your name.';
  if (!EMAIL_RE.test((c?.email ?? '').trim()) || (c?.email ?? '').length > 254) return 'Please enter a valid email address.';
  return null;
}

/** Returns an error message, or null if the selection is bookable. Used by the UI and re-checked by the server. */
export function validateSelection(sel: BookingSelection): string | null {
  let people = 0;
  for (const day of DAYS) {
    for (const { id: type } of MEAL_TYPES) {
      for (const { id: age } of AGE_GROUPS) {
        const n = count(sel, day.date, type, age);
        if (!isCount(n)) return 'Invalid number of meals.';
        if (n > 0 && !day.menus[type]) return `There is no ${type === 'veg' ? 'vegetarian' : 'non-vegetarian'} menu on ${day.name}.`;
        if (n > 0 && day.soldOut) return `${day.name} is fully booked - we're no longer able to take food registrations for that day.`;
        people += n;
      }
    }
  }
  for (const { id: age } of AGE_GROUPS) {
    const n = sel.anandamela?.[age] ?? 0;
    if (!isCount(n)) return 'Invalid number of Anandamela attendees.';
    if (n > 0 && ANANDAMELA.soldOut) return `${ANANDAMELA.name} is fully booked - we're no longer able to take further registrations.`;
    people += n;
  }
  if (people === 0) return 'Please select at least one meal or Anandamela attendee.';
  const d = sel.donation ?? 0;
  if (typeof d !== 'number' || !Number.isFinite(d) || d < 0 || d > MAX_DONATION || Math.abs(d * 100 - Math.round(d * 100)) > 1e-6) {
    return `Please enter a donation between €1 and €${MAX_DONATION} (or untick the donation).`;
  }
  if (d > 0 && d < 1) return 'The minimum donation is €1.';
  return null;
}

export const validateBooking = (req: BookingRequest) => validateCustomer(req?.customer) ?? validateSelection(req);

/** Prices a selection from the menu above. The server calls this too, so the invoice can never differ from what the user saw. */
export function quote(sel: BookingSelection): Quote {
  const lines: QuoteLine[] = [];
  for (const day of DAYS) {
    for (const { id: type } of MEAL_TYPES) {
      const menu = day.menus[type];
      if (!menu) continue;
      for (const age of AGE_GROUPS) {
        const quantity = count(sel, day.date, type, age.id);
        if (quantity === 0) continue;
        const unitCents = toCents(menu.prices[age.id]);
        lines.push({
          date: day.date, mealType: type, ageGroup: age.id, quantity, unitCents,
          amountCents: unitCents * quantity,
          description: `${day.dateLabel} · ${day.name} - ${menu.name} - ${age.name} (${age.range})`,
        });
      }
    }
  }
  const anandamela: AnandamelaLine[] = [];
  for (const age of AGE_GROUPS) {
    const quantity = sel.anandamela?.[age.id] ?? 0;
    if (quantity === 0) continue;
    const unitCents = toCents(ANANDAMELA.prices[age.id]);
    anandamela.push({
      ageGroup: age.id, quantity, unitCents, amountCents: unitCents * quantity,
      description: `${ANANDAMELA.dateLabel} · ${ANANDAMELA.name} registration - ${age.name} (${age.range})`,
    });
  }

  const mealsCents = lines.reduce((sum, l) => sum + l.amountCents, 0);
  const anandamelaCents = anandamela.reduce((sum, l) => sum + l.amountCents, 0);
  const donationCents = toCents(sel.donation || 0);
  const totalCents = mealsCents + anandamelaCents + donationCents;
  return { lines, anandamela, mealsCents, anandamelaCents, donationCents, totalCents, feeCents: estimateFeeCents(totalCents) };
}

// Compact form of a selection for Stripe metadata (500 chars per value): counts in DAYS x MEAL_TYPES x AGE_GROUPS order.
export const encodeSelection = (sel: BookingSelection): string =>
  JSON.stringify({
    m: DAYS.map((d) => MEAL_TYPES.flatMap((t) => AGE_GROUPS.map((a) => count(sel, d.date, t.id, a.id)))),
    a: AGE_GROUPS.map((a) => sel.anandamela?.[a.id] ?? 0),
    d: toCents(sel.donation || 0),
  });

export function decodeSelection(encoded: string): BookingSelection {
  const { m, a, d } = JSON.parse(encoded);
  const sel = emptySelection();
  DAYS.forEach((day, i) => MEAL_TYPES.forEach((t, j) => AGE_GROUPS.forEach((age, k) => (sel.meals[day.date][t.id][age.id] = m[i][j * AGE_GROUPS.length + k]))));
  AGE_GROUPS.forEach((age, k) => (sel.anandamela[age.id] = a[k]));
  sel.donation = d / 100;
  return sel;
}

/** Builds the Firestore document. `paid` carries the Stripe ids; omit it for a free (nothing to pay) reservation. */
export function toReservation(
  id: string,
  customerInfo: Reservation2026['customerInfo'],
  sel: BookingSelection,
  paid?: { amountCents: number; invoiceId: string; invoiceNumber?: string; paymentIntentId?: string }
): Omit<Reservation2026, 'createdAt'> {
  const meals: Reservation2026['meals'] = {};
  for (const day of DAYS) {
    if (MEAL_TYPES.some((t) => AGE_GROUPS.some((a) => count(sel, day.date, t.id, a.id) > 0))) {
      meals[day.date] = MEAL_TYPES.reduce((acc, t) => ({ ...acc, [t.id]: Object.fromEntries(AGE_GROUPS.map((a) => [a.id, count(sel, day.date, t.id, a.id)])) }), {} as DayMeals);
    }
  }
  return {
    id, eventType: EVENT_NAME, customerInfo, meals,
    anandamela: { ...emptyCounts(), ...sel.anandamela },
    donation: sel.donation || 0,
    totalAmount: (paid ? paid.amountCents : 0) / 100,
    currency: 'eur',
    paymentStatus: paid ? 'paid' : 'free',
    ...(paid && { invoiceId: paid.invoiceId, invoiceNumber: paid.invoiceNumber, paymentIntentId: paid.paymentIntentId }),
  } as Omit<Reservation2026, 'createdAt'>;
}
